import React, { useState, useEffect } from 'react';
import type { ExplanationGuidelines } from '../types';

interface ExplanationGuidelinesModalProps {
  isOpen: boolean;
  onClose: () => void;
  guidelines: ExplanationGuidelines;
  onAddGuideline: (guideline: string) => void;
  onUpdateGuideline: (index: number, newText: string) => void;
  onDeleteGuideline: (index: number) => void;
}

const ExplanationGuidelinesModal: React.FC<ExplanationGuidelinesModalProps> = ({ 
  isOpen, onClose, guidelines, 
  onAddGuideline, onUpdateGuideline, onDeleteGuideline 
}) => {
  const [newGuideline, setNewGuideline] = useState('');
  const [editingGuideline, setEditingGuideline] = useState<{ index: number; text: string } | null>(null);

  useEffect(() => {
    const handleEsc = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        onClose();
      }
    };
    window.addEventListener('keydown', handleEsc);
    return () => {
      window.removeEventListener('keydown', handleEsc);
    };
  }, [onClose]);

  if (!isOpen) {
    return null;
  }
  
  const handleAdd = () => {
    if (newGuideline.trim()) {
      onAddGuideline(newGuideline);
      setNewGuideline('');
    }
  };

  const handleUpdate = () => {
    if (editingGuideline) {
      onUpdateGuideline(editingGuideline.index, editingGuideline.text);
      setEditingGuideline(null);
    }
  };

  return (
    <div 
      className="fixed inset-0 bg-black bg-opacity-75 flex justify-center items-center z-50 p-4"
      onClick={onClose}
    >
      <div 
        className="bg-[#1A1D2D] text-[#F0EAD6] rounded-lg shadow-xl w-full max-w-4xl max-h-[90vh] overflow-y-auto p-8 relative"
        onClick={(e) => e.stopPropagation()}
      >
        <button 
          onClick={onClose}
          className="absolute top-4 right-4 text-[#A9B1D6] hover:text-white text-3xl leading-none"
          aria-label="Close"
        >
          &times;
        </button>

        <div className="space-y-8">
          <div>
            <h2 className="text-2xl font-bold text-sky-400 mb-2">해설자 행동 강령</h2>
            <p className="text-[#A9B1D6]">AI가 변형문제 해설을 생성할 때 사용할 규칙을 관리합니다. 이 규칙은 AI에게 절대적인 영향을 미칩니다.</p>
          </div>

          <div className="bg-[#252A41] p-6 rounded-lg shadow-inner space-y-4">
            <div className="flex flex-col sm:flex-row gap-2 pt-2">
              <textarea
                value={newGuideline}
                onChange={(e) => setNewGuideline(e.target.value)}
                placeholder="새로운 강령을 입력하세요... (Shift+Enter로 줄바꿈)"
                onKeyPress={(e) => {
                    if (e.key === 'Enter' && !e.shiftKey) {
                        e.preventDefault();
                        handleAdd();
                    }
                }}
                className="flex-grow bg-[#1A1D2D] border border-[#5F6B8E] rounded-md py-2 px-3 text-white focus:outline-none focus:ring-2 focus:ring-sky-500 h-24"
              />
              <button onClick={handleAdd} className="w-full sm:w-auto bg-sky-500 hover:bg-sky-600 text-white font-bold py-2 px-4 rounded-lg transition-colors duration-300">추가</button>
            </div>
            
            {(guidelines?.guidelines ?? []).length === 0 ? (
              <p className="text-[#5F6B8E] text-center pt-4">설정된 강령이 없습니다.</p>
            ) : (
              <ul className="space-y-3 pt-4">
                {guidelines.guidelines.map((guideline, index) => (
                  <li key={index} className="bg-[#1A1D2D] p-3 rounded-md flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
                    {editingGuideline?.index === index ? (
                      <textarea value={editingGuideline.text} onChange={(e) => setEditingGuideline({ ...editingGuideline, text: e.target.value })} className="flex-grow bg-[#252A41] border border-sky-500 rounded-md py-1 px-2 text-white w-full h-28" />
                    ) : (
                      <p className="text-[#A9B1D6] flex-grow break-all whitespace-pre-wrap"><span className="font-bold text-sky-400 mr-2">{index + 1}.</span>{guideline}</p>
                    )}
                    <div className="flex-shrink-0 flex gap-2 self-end sm:self-center">
                      {editingGuideline?.index === index ? (
                        <>
                          <button onClick={handleUpdate} className="text-sm text-green-400 hover:text-green-300">저장</button>
                          <button onClick={() => setEditingGuideline(null)} className="text-sm text-gray-400 hover:text-gray-300">취소</button>
                        </>
                      ) : (
                        <>
                          <button onClick={() => setEditingGuideline({ index, text: guideline })} className="text-sm text-[#A9B1D6] hover:text-white">수정</button>
                          <button onClick={() => onDeleteGuideline(index)} className="text-sm text-rose-400 hover:text-rose-300">삭제</button>
                        </>
                      )}
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ExplanationGuidelinesModal;
